import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToQuote = () => {
    navigate('/solar-quote');
    setMobileOpen(false);
  };

  const openBooking = () => {
    window.dispatchEvent(new CustomEvent('openBooking'));
    setMobileOpen(false);
  };

  const navLinks = [
    { label: 'Home', href: '/', icon: 'ri-home-4-line' },
    { label: 'Solar', href: '/solar', icon: 'ri-sun-line' },
    { label: 'Battery', href: '/battery', icon: 'ri-battery-2-charge-line' },
    { label: 'EV Charger', href: '/ev-charger', icon: 'ri-charging-pile-line' },
    { label: 'Repair', href: '/#repairs', icon: 'ri-tools-line' },
    { label: 'Projects', href: '/projects', icon: 'ri-image-2-line' },
    { label: 'Contact', href: '/contact', icon: 'ri-mail-line' },
  ];

  const desktopTopLinks = [
    { label: 'Home', href: '/' },
    { label: 'Projects', href: '/projects' },
    { label: 'Contact', href: '/contact' },
  ];

  const serviceLinks = [
    { label: 'Solar', href: '/solar', icon: 'ri-sun-line', desc: 'Panels & installation' },
    { label: 'Battery', href: '/battery', icon: 'ri-battery-2-charge-line', desc: 'Storage systems' },
    { label: 'EV Charger', href: '/ev-charger', icon: 'ri-charging-pile-line', desc: 'Home & commercial' },
    { label: 'Repair', href: '/#repairs', icon: 'ri-tools-line', desc: 'Servicing & fixes' },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${scrolled ? 'border-b border-gray-100' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-24 md:h-40">

        {/* Logo — much larger */}
        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          <img
            src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/ee62e899e0ea98d48397a860148ed960.png"
            alt="Renewables Made Easy"
            className="h-20 md:h-36 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav — Services dropdown */}
        <nav className="hidden md:flex items-center gap-0.5">
          <Link
            to="/"
            className="px-3 py-2 font-black text-[13px] text-[#145A32] rounded-xl hover:bg-[#145A32] hover:text-white transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Home
          </Link>

          {/* Services Dropdown */}
          <div ref={servicesRef} className="relative">
            <button
              onClick={() => setServicesOpen((o) => !o)}
              className="flex items-center gap-1 px-3 py-2 font-black text-[13px] text-[#145A32] rounded-xl hover:bg-[#145A32] hover:text-white transition-all duration-200 cursor-pointer whitespace-nowrap group"
            >
              Services
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
              </div>
            </button>

            {servicesOpen && (
              <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-gray-100 rounded-2xl shadow-lg overflow-hidden z-50">
                {serviceLinks.map((s) => (
                  <Link
                    key={s.label}
                    to={s.href}
                    onClick={() => setServicesOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-[#145A32]/5 transition-colors cursor-pointer group border-b border-gray-50 last:border-0"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#145A32]/10 group-hover:bg-[#145A32]/20 flex-shrink-0">
                      <i className={`${s.icon} text-[#145A32] text-sm`} />
                    </div>
                    <div>
                      <div className="font-black text-[13px] text-[#145A32]">{s.label}</div>
                      <div className="text-[11px] text-gray-400">{s.desc}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {desktopTopLinks.slice(1).map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className="px-3 py-2 font-black text-[13px] text-[#145A32] rounded-xl hover:bg-[#145A32] hover:text-white transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Desktop CTA buttons */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="tel:03300101234"
            className="flex items-center gap-2 font-bold text-sm text-[#145A32] hover:text-[#4CAF50] transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#145A32] animate-pulse-green">
              <i className="ri-phone-line text-white text-sm" />
            </div>
            0330 010 1234
          </a>

          {/* Book Appointment button */}
          <button
            onClick={openBooking}
            className="flex items-center gap-2 bg-[#f59e0b] text-white font-black px-5 py-2.5 rounded-full text-sm hover:bg-[#d97706] transition-all whitespace-nowrap cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-check-line text-sm" />
            </div>
            Book
          </button>

          <button
            onClick={scrollToQuote}
            className="shimmer-btn text-white font-black px-6 py-2.5 rounded-full text-sm hover:opacity-90 transition-all whitespace-nowrap cursor-pointer"
          >
            Get a Quote &rarr;
          </button>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 cursor-pointer p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <span className={`block w-6 h-0.5 bg-[#145A32] transition-all duration-200 ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
          <span className={`block w-6 h-0.5 bg-[#145A32] transition-all duration-200 ${mobileOpen ? 'opacity-0' : ''}`} />
          <span className={`block w-6 h-0.5 bg-[#145A32] transition-all duration-200 ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`md:hidden bg-white border-t-2 border-[#4CAF50]/20 overflow-hidden transition-all duration-300 ${mobileOpen ? 'max-h-[36rem]' : 'max-h-0'}`}>
        <div className="px-4 pb-5 pt-2 flex flex-col gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className="flex items-center gap-3 text-[#145A32] font-black py-3 px-4 border-b border-gray-100 cursor-pointer hover:bg-[#145A32] hover:text-white transition-all rounded-xl group"
              onClick={() => setMobileOpen(false)}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#145A32]/10 group-hover:bg-white/20">
                <i className={`${link.icon} text-[#145A32] text-sm group-hover:text-white`} />
              </div>
              <span className="flex-1">{link.label}</span>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-[#4CAF50] text-lg group-hover:text-white" />
              </div>
            </Link>
          ))}

          {/* Mobile booking CTA */}
          <button
            onClick={openBooking}
            className="mt-3 w-full flex items-center justify-center gap-2 bg-[#f59e0b] text-white font-black px-6 py-3.5 rounded-full text-sm cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-check-line text-sm" />
            </div>
            Book an Appointment
            <span className="bg-white/20 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">GHL</span>
          </button>

          <button
            onClick={scrollToQuote}
            className="w-full shimmer-btn text-white font-black px-6 py-3.5 rounded-full text-sm cursor-pointer whitespace-nowrap"
          >
            Get a Quote &rarr;
          </button>

          {/* Phone */}
          <a href="tel:03300101234" className="mt-1 flex items-center justify-center gap-2 text-[#145A32] font-bold text-sm py-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-phone-line text-[#145A32]" />
            </div>
            0330 010 1234
          </a>
        </div>
      </div>
    </header>
  );
}
